import { useState, useEffect, useCallback, useRef } from "react";
import { fetchPage } from "@/lib/api";

const PAGE_SIZE = 24;

/**
 * Paginated collection with "load more".
 * Refetches from scratch whenever params change (topic, search, sort).
 */
export function usePagedCollection(resource, params = {}, pageSize = PAGE_SIZE) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [hasMore, setHasMore]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [loadingMore, setMore]  = useState(false);

  const paramsKey = JSON.stringify(params);
  const mounted = useRef(true);
  const offset  = useRef(0);

  // Reset and load page 1 whenever the filters change
  useEffect(() => {
    mounted.current = true;
    offset.current = 0;
    setLoading(true);

    fetchPage(resource, { ...params, limit: pageSize, offset: 0 })
      .then(({ items, total, hasMore }) => {
        if (!mounted.current) return;
        setItems(items);
        setTotal(total);
        setHasMore(hasMore);
        offset.current = items.length;
      })
      .catch(() => {
        if (!mounted.current) return;
        setItems([]); setTotal(0); setHasMore(false);
      })
      .finally(() => { if (mounted.current) setLoading(false); });

    return () => { mounted.current = false; };
  }, [resource, paramsKey, pageSize]); // eslint-disable-line

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setMore(true);
    try {
      const page = await fetchPage(resource, {
        ...params, limit: pageSize, offset: offset.current,
      });
      setItems(prev => {
        // Guard against duplicates if a new post landed mid-scroll
        const seen = new Set(prev.map(p => p.id));
        return [...prev, ...page.items.filter(p => !seen.has(p.id))];
      });
      setTotal(page.total);
      setHasMore(page.hasMore);
      offset.current += page.items.length;
    } catch {
      setHasMore(false);
    } finally {
      setMore(false);
    }
  }, [resource, paramsKey, pageSize, hasMore, loadingMore]); // eslint-disable-line

  const prepend = useCallback((item) => {
    setItems(prev => [item, ...prev]);
    setTotal(t => t + 1);
    offset.current += 1;
  }, []);

  return { items, total, hasMore, loading, loadingMore, loadMore, prepend };
}
